// Payroll Register — a rendered report for a locked run, in the KaziPeople theme,
// with Export to Excel + Export to PDF. Data comes from payroll-service /register
// (already-computed figures); the column set is self-describing per run.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Printer, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPayrollRegister, downloadPayrollPackage, type PayrollRegister, type RegisterGroup } from '../api/register';
import { getApiErrorMessage } from '@/lib/apiError';

const GROUP_LABEL: Record<RegisterGroup, string> = {
  earning: 'Earnings',
  deduction: 'Deductions',
  net: 'Net',
  employer: 'Employer contributions',
};

const fmt = (s?: string) => Math.round(Number(s || 0)).toLocaleString('en-US');

function humanPeriod(p: string) {
  const m = /^(\d{4})-(\d{2})/.exec(p);
  if (!m) return p;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function initialsOf(company: string) {
  return (company.match(/[A-Za-z]\S*/g) || []).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'CO';
}

export const PayrollRegisterPage: React.FC = () => {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const [reg, setReg] = useState<PayrollRegister | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pkgBusy, setPkgBusy] = useState(false);

  useEffect(() => {
    getPayrollRegister(runId)
      .then(setReg)
      .catch((e) => setError(e?.response?.data?.message || 'Could not load the payroll register.'))
      .finally(() => setLoading(false));
  }, [runId]);

  // Grouped header spans (columns arrive already ordered by group).
  const groups = useMemo(() => {
    const out: { group: RegisterGroup; span: number }[] = [];
    for (const c of reg?.columns ?? []) {
      const last = out[out.length - 1];
      if (last && last.group === c.group) last.span++;
      else out.push({ group: c.group, span: 1 });
    }
    return out;
  }, [reg]);

  const statutory = useMemo(() => {
    if (!reg) return [];
    const t = reg.totals;
    const items: { label: string; amt: number }[] = [];
    if (Number(t.PAYE)) items.push({ label: 'PAYE', amt: Number(t.PAYE) });
    const nssf = Number(t.NSSF_EE || 0) + Number(t.NSSF_ER || 0);
    if (nssf) items.push({ label: 'NSSF', amt: nssf });
    (['SDL', 'WCF', 'HESLB', 'NHIF'] as const).forEach((k) => {
      if (Number(t[k])) items.push({ label: k, amt: Number(t[k]) });
    });
    return items;
  }, [reg]);
  const statMax = Math.max(1, ...statutory.map((s) => s.amt));

  // Non-statutory employer-paid benefit contributions (e.g. a medical-plan
  // employer share) — surfaced as their own bars so they're not invisible next
  // to the statutory remittances. Keyed off the register's employer columns.
  const employerBenefits = useMemo(() => {
    if (!reg) return [];
    const statKeys = new Set(['NSSF_ER', 'SDL', 'WCF', 'HESLB', 'NHIF']);
    return reg.columns
      .filter((c) => c.group === 'employer' && !statKeys.has(c.key))
      .map((c) => ({ label: c.label, amt: Number(reg.totals[c.key] || 0) }))
      .filter((x) => x.amt);
  }, [reg]);
  const ebMax = Math.max(1, ...employerBenefits.map((s) => s.amt));

  const exportExcel = () => {
    if (!reg) return;
    const xesc = (s: any) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const cell = (v: any, type: string) => `<Cell><Data ss:Type="${type}">${xesc(v)}</Data></Cell>`;
    const headCells = ['No', 'Employee', ...reg.columns.map((c) => c.label)].map((h) => cell(h, 'String')).join('');
    const body = reg.employees
      .map((r) => {
        const cells = [cell(r.no, 'Number'), cell(r.name, 'String'), ...reg.columns.map((c) => cell(Number(r.values[c.key] || 0), 'Number'))];
        return `<Row>${cells.join('')}</Row>`;
      })
      .join('');
    const totalCells = [cell('Grand Total', 'String'), cell(`${reg.employeeCount}`, 'String'), ...reg.columns.map((c) => cell(Number(reg.totals[c.key] || 0), 'Number'))];
    const xml = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Payroll Register"><Table>
<Row>${cell('Company :', 'String')}${cell(reg.company, 'String')}</Row>
<Row>${cell('Period :', 'String')}${cell(humanPeriod(reg.period), 'String')}${cell('Payroll Register', 'String')}</Row>
<Row></Row>
<Row>${headCells}</Row>
${body}
<Row>${totalCells.join('')}</Row>
</Table></Worksheet></Workbook>`;
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `Payroll_Register_${(reg.company.split(' ')[0] || 'company')}_${reg.period}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadPackage = async () => {
    if (!reg) return;
    setPkgBusy(true);
    try {
      await downloadPayrollPackage(runId, `payroll-package-${reg.company.split(' ')[0] || 'company'}-${reg.period}.zip`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not build the payroll package.'));
    } finally {
      setPkgBusy(false);
    }
  };

  if (loading) return <div className="reg-loading">Loading payroll register…</div>;
  if (error || !reg) return <div className="reg-loading">{error || 'Not found.'}</div>;

  const cur = reg.currency;

  return (
    <div className="reg">
      <style>{REG_CSS}</style>

      <div className="reg-toolbar">
        <button className="reg-btn ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} />Back</button>
        <div className="reg-tt">
          <div className="reg-tt-title">Payroll Register</div>
          <div className="reg-tt-sub">{reg.company}</div>
        </div>
        <div className="reg-spacer" />
        <div className="reg-period">{humanPeriod(reg.period)}</div>
        <button className="reg-btn" onClick={exportExcel}><FileSpreadsheet size={15} />Export to Excel</button>
        <button className="reg-btn" onClick={() => window.print()}><Printer size={15} />Export to PDF</button>
        <button className="reg-btn primary" onClick={downloadPackage} disabled={pkgBusy}><Package size={15} />{pkgBusy ? 'Preparing…' : 'Download package'}</button>
      </div>

      <div className="reg-wrap">
        <div className="reg-paper">
          {/* masthead */}
          <div className="reg-mast">
            <div className="reg-mast-row">
              <div>
                <div className="reg-eyebrow">Payroll Register · {reg.runState} run</div>
                <div className="reg-company">{reg.company}</div>
                <div className="reg-meta">Pay period <b>{humanPeriod(reg.period)}</b> · {reg.employeeCount} employees · {cur}</div>
              </div>
              {reg.logoUrl
                ? <div className="reg-seal"><img src={reg.logoUrl} alt={`${reg.company} logo`} /></div>
                : <div className="reg-seal mono"><div><b>{initialsOf(reg.company)}</b><span>{reg.company.split(' ')[0]}</span></div></div>}
            </div>
          </div>

          {/* summary */}
          <div className="reg-section">
            <div className="reg-stats">
              <div className="reg-stat"><div className="k">Employees</div><div className="v">{reg.employeeCount}</div></div>
              <div className="reg-stat"><div className="k">Gross pay</div><div className="v mono">{fmt(reg.totals.gross)}<span className="cur">{cur}</span></div></div>
              <div className="reg-stat"><div className="k">Deductions</div><div className="v mono">{fmt(reg.totals.total_deductions)}<span className="cur">{cur}</span></div></div>
              <div className="reg-stat hi"><div className="k">Net pay</div><div className="v mono">{fmt(reg.totals.net)}<span className="cur">{cur}</span></div></div>
              <div className="reg-stat"><div className="k">Employer cost</div><div className="v mono">{fmt(reg.employerCost)}<span className="cur">{cur}</span></div></div>
            </div>
          </div>

          {/* statutory */}
          {statutory.length > 0 && (
            <div className="reg-section">
              <div className="reg-sec-head"><h2>Statutory contributions</h2><span className="hint">to remit this month · {cur}</span></div>
              <div className="reg-bars">
                {statutory.map((s) => (
                  <div className="reg-bar" key={s.label}>
                    <div className="lab">{s.label}</div>
                    <div className="track"><div className="fill" style={{ width: `${Math.max(4, (s.amt / statMax) * 100)}%` }} /></div>
                    <div className="amt mono">{fmt(String(s.amt))}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* employer benefit contributions (non-statutory) */}
          {employerBenefits.length > 0 && (
            <div className="reg-section">
              <div className="reg-sec-head"><h2>Employer benefit contributions</h2><span className="hint">employer-paid benefits · {cur}</span></div>
              <div className="reg-bars">
                {employerBenefits.map((s) => (
                  <div className="reg-bar" key={s.label}>
                    <div className="lab">{s.label}</div>
                    <div className="track"><div className="fill" style={{ width: `${Math.max(4, (s.amt / ebMax) * 100)}%` }} /></div>
                    <div className="amt mono">{fmt(String(s.amt))}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* register table */}
          <div className="reg-section">
            <div className="reg-sec-head"><h2>Register</h2><span className="hint">every employee · figures in {cur}</span></div>
            <div className="reg-tablewrap">
              <table>
                <thead>
                  <tr className="grp">
                    <th className="l" />
                    {groups.map((g, i) => <th key={i} className={`g-${g.group}`} colSpan={g.span}>{GROUP_LABEL[g.group]}</th>)}
                  </tr>
                  <tr className="cols">
                    <th className="l">Employee</th>
                    {reg.columns.map((c, i) => <th key={c.key} className={i === 0 ? 'sep' : ''}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {reg.employees.map((r) => (
                    <tr key={r.no}>
                      <td className="l"><span className="no">{r.no}</span><span className="nm">{r.name}</span></td>
                      {reg.columns.map((c, i) => (
                        <td key={c.key} className={`mono ${i === 0 ? 'sep' : ''} ${c.subtotal ? 'sub' : ''} ${c.group === 'net' ? 'net' : ''}`}>
                          {fmt(r.values[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="l">Grand total · {reg.employeeCount}</td>
                    {reg.columns.map((c, i) => (
                      <td key={c.key} className={`mono ${i === 0 ? 'sep' : ''} ${c.group === 'net' ? 'net' : ''}`}>{fmt(reg.totals[c.key])}</td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="reg-foot">
            <span>KaziPeople Payroll · figures are system-computed from a <b>{reg.runState}</b> run</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const REG_CSS = `
.reg{--o:#F28A3A;--od:#D9711E;--ot:#FEF0E4;--ch:#2F3443;--cream:#FBF6F1;--muted:#F3EBE0;--mfg:#7A8099;--bd:rgba(47,52,67,.12);--bds:rgba(47,52,67,.22);--grn:#15803D;--red:#DC2626;--info:#2563EB;
  --grad:linear-gradient(135deg,#F28A3A 0%,#F6B26B 100%);color:var(--ch);}
.reg-loading{padding:60px;text-align:center;color:#7A8099}
.reg .mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-variant-numeric:tabular-nums;letter-spacing:-.01em}
.reg-toolbar{position:sticky;top:0;z-index:20;display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding:12px 20px;background:rgba(251,246,241,.9);backdrop-filter:blur(8px);border-bottom:1px solid var(--bd)}
.reg-tt-title{font-weight:800;font-size:14px}.reg-tt-sub{color:var(--mfg);font-size:12px;font-weight:600}
.reg-spacer{flex:1}
.reg-period{font-size:13px;font-weight:700;color:var(--mfg);background:#fff;border:1px solid var(--bd);border-radius:9px;padding:7px 12px}
.reg-btn{font-weight:700;font-size:13px;border-radius:9px;padding:8px 13px;cursor:pointer;border:1px solid var(--bd);background:#fff;color:var(--ch);display:inline-flex;gap:7px;align-items:center}
.reg-btn:hover{box-shadow:0 3px 10px rgba(47,52,67,.08)}
.reg-btn:disabled{opacity:.6;cursor:default;box-shadow:none}
.reg-btn.ghost{background:transparent;border-color:transparent;color:var(--mfg)}
.reg-btn.primary{background:var(--grad);color:#fff;border-color:transparent;box-shadow:0 4px 12px rgba(217,113,30,.3)}
.reg-wrap{max-width:1160px;margin:24px auto 60px;padding:0 20px}
.reg-paper{background:#fff;border:1px solid var(--bd);border-radius:20px;overflow:hidden;box-shadow:0 24px 60px -30px rgba(47,52,67,.28)}
.reg-mast{padding:28px 32px 22px;position:relative}
.reg-mast::before{content:"";position:absolute;left:0;top:0;height:4px;width:100%;background:var(--grad)}
.reg-mast-row{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;flex-wrap:wrap}
.reg-eyebrow{font-size:11px;font-weight:800;letter-spacing:.15em;text-transform:uppercase;color:var(--od)}
.reg-company{font-size:25px;font-weight:800;letter-spacing:-.02em;margin:6px 0 2px}
.reg-meta{color:var(--mfg);font-size:13px;font-weight:500}
.reg-seal{width:76px;height:76px;border-radius:16px;background:#fff;border:1px solid var(--bd);display:grid;place-items:center;overflow:hidden;padding:8px;flex-shrink:0}
.reg-seal img{max-width:100%;max-height:100%;object-fit:contain}
.reg-seal.mono{background:var(--ot);border-color:rgba(217,113,30,.25)}
.reg-seal b{display:block;font-size:24px;font-weight:800;color:var(--od);line-height:1}
.reg-seal span{font-size:8.5px;font-weight:800;letter-spacing:.1em;color:var(--od);text-transform:uppercase;margin-top:5px}
.reg-section{padding:24px 32px;border-top:1px solid var(--bd)}
.reg-sec-head{display:flex;align-items:baseline;gap:10px;margin-bottom:15px}
.reg-sec-head h2{font-size:15px;font-weight:800;margin:0}.reg-sec-head .hint{font-size:12px;color:var(--mfg)}
.reg-stats{display:grid;grid-template-columns:repeat(5,1fr);gap:12px}
.reg-stat{border:1px solid var(--bd);border-radius:14px;padding:14px 15px}
.reg-stat .k{font-size:11px;font-weight:700;color:var(--mfg);text-transform:uppercase;letter-spacing:.05em}
.reg-stat .v{font-size:19px;font-weight:800;margin-top:6px;letter-spacing:-.02em}
.reg-stat .cur{font-size:11px;color:var(--mfg);font-weight:700;margin-left:3px}
.reg-stat.hi{background:linear-gradient(135deg,#FFF6EE,#FEF0E4);border-color:rgba(217,113,30,.28)}
.reg-stat.hi .v{color:var(--od)}
.reg-bars{display:flex;flex-direction:column;gap:11px}
.reg-bar{display:grid;grid-template-columns:120px 1fr 120px;align-items:center;gap:14px}
.reg-bar .lab{font-size:13px;font-weight:700}
.reg-bar .track{height:11px;border-radius:999px;background:var(--muted);overflow:hidden}
.reg-bar .fill{height:100%;border-radius:999px;background:var(--grad)}
.reg-bar .amt{text-align:right;font-weight:800;font-size:13px}
.reg-tablewrap{overflow-x:auto;border:1px solid var(--bd);border-radius:14px}
.reg table{border-collapse:collapse;width:100%;min-width:820px;font-size:12.5px}
.reg thead .grp th{font-size:10px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:var(--mfg);padding:10px 12px 4px;text-align:center;border-bottom:1px solid var(--bd)}
.reg thead .grp th.g-earning{color:var(--od)}.reg thead .grp th.g-deduction{color:var(--red)}.reg thead .grp th.g-net{color:var(--grn)}.reg thead .grp th.g-employer{color:var(--info)}
.reg thead .cols th{position:sticky;top:0;background:#fff;font-size:11px;font-weight:700;padding:8px 12px;border-bottom:2px solid var(--bds);text-align:right;white-space:nowrap}
.reg thead .cols th.l{text-align:left}
.reg tbody td{padding:9px 12px;border-bottom:1px solid var(--bd);text-align:right;white-space:nowrap}
.reg td.l,.reg th.l{text-align:left}
.reg tbody tr:nth-child(even){background:#FCFAF7}
.reg tbody tr:hover{background:var(--ot)}
.reg td.sub{font-weight:800}
.reg td.net{font-weight:800;color:var(--grn)}
.reg .sep{border-left:1px solid var(--bd)}
.reg .no{display:inline-grid;place-items:center;min-width:22px;height:20px;padding:0 6px;border-radius:6px;background:var(--muted);color:var(--mfg);font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;margin-right:9px}
.reg .nm{font-weight:700}
.reg tfoot td{padding:12px;border-top:2px solid var(--bds);font-weight:800;text-align:right;background:#FFF9F3}
.reg tfoot td.l{text-align:left;color:var(--od)}
.reg-foot{padding:16px 32px 24px;color:var(--mfg);font-size:11.5px}
@media (max-width:820px){.reg-stats{grid-template-columns:repeat(2,1fr)}.reg-bar{grid-template-columns:96px 1fr 96px}}
@media print{
  .reg-toolbar{display:none}
  .reg-wrap{margin:0;padding:0;max-width:none}
  .reg-paper{border:none;border-radius:0;box-shadow:none}
  .reg tbody tr:hover{background:transparent}
  .reg-section{break-inside:avoid}
  thead{display:table-header-group}tr{break-inside:avoid}
}
`;

export default PayrollRegisterPage;
